import { FocusKeyManager } from '@angular/cdk/a11y';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ChangeDetectorRef, QueryList } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ApolloQueryResult } from '@apollo/client';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, map, startWith, takeUntil, tap } from 'rxjs/operators';

import { QueryRef } from 'apollo-angular';

import { CustomInfiniteScrollNextPageManager } from 'shared/CustomInfiniteScrollNextPageManager';

import { ListItemComponent } from './components/list-item/list-item.component';

export interface InfiniteSearchScrollableComponentTemplate<T> {
  disposer: Subject<any>;
  filterInput: UntypedFormControl;
  loading: boolean;
  itemsPerPage: number;
  selectResults(apolloQueryResult: ApolloQueryResult<any>): T[];
  resultsQuery: QueryRef<any>;
  items: QueryList<ListItemComponent>;
  getScrollContainer(): CdkVirtualScrollViewport;
  getFetchMoreVariables(page: number): any;
  getChangeDetectorRef(): ChangeDetectorRef;
}

/**
 *
 */

export class InfiniteSearchScrollAdaptor<T> {
  nextPageManager: CustomInfiniteScrollNextPageManager<any>;
  keyManager: FocusKeyManager<ListItemComponent>;

  private _currentResults: T[] = [];
  private _results = new BehaviorSubject<T[]>([]);
  public results = this._results.asObservable();

  private get cd() {
    return this._component.getChangeDetectorRef();
  }

  constructor(
    private _component: InfiniteSearchScrollableComponentTemplate<T>,
  ) {
  }

  connect() {
    const component = this._component;
    this._component.filterInput.valueChanges
      .pipe(
        takeUntil(component.disposer),
        debounceTime(500)
      )
      .subscribe(() => this.reset());

    this.nextPageManager = new CustomInfiniteScrollNextPageManager<any>(
      component.getScrollContainer(),
      component.itemsPerPage
    );

    this.keyManager = new FocusKeyManager(component.items);

    this.nextPageManager
      .nextPageNeeded.pipe(
        takeUntil(this._component.disposer),
        startWith(0),
        tap(() => {
          component.loading = true;
          this.cd.detectChanges();
        })
      )
      .subscribe((page: number ) => {
        const variables = component.getFetchMoreVariables(page);
        component.resultsQuery.setVariables(variables);
      }, (err) => {
        component.loading = false;
      });

    this._component.resultsQuery.valueChanges.pipe(
      takeUntil(this._component.disposer),
      map((res) => this._component.selectResults(res))
    ).subscribe((next) => {
      this._currentResults = [...this._currentResults as any,
        ...next
      ];

      this._results.next(this._currentResults);
      component.loading = false;
    });
  }

  reset(): void {
    this.keyManager.updateActiveItem(null);
    this._currentResults = [];
    this._component.resultsQuery.resetLastResults();
    this._component.resultsQuery.setVariables(this._component.getFetchMoreVariables(0));
    this.cd.detectChanges();
    this.cd.markForCheck();
    this.nextPageManager.reset();
  }
}
